// src/registerServiceWorker.ts

import { shouldRegisterBrowserServiceWorker } from './utils/pushEnvironment';

const DEV_SERVICE_WORKER_ENABLED = import.meta.env.VITE_ENABLE_SERVICE_WORKER_IN_DEV === 'true';
const MANAGED_CACHE_PREFIXES = ['todo-app-cache-', 'workbox-'];

async function cleanupDevelopmentRegistrations(): Promise<void> {
  const registrations = await navigator.serviceWorker.getRegistrations();
  await Promise.all(
    registrations.map(async (registration) => {
      try {
        await registration.unregister();
      } catch (error) {
        console.warn('Service Worker Unregister in Entwicklung fehlgeschlagen:', error);
      }
    }),
  );

  if ('caches' in window) {
    const cacheKeys = await caches.keys();
    await Promise.all(
      cacheKeys
        .filter((cacheName) => MANAGED_CACHE_PREFIXES.some((prefix) => cacheName.startsWith(prefix)))
        .map(async (cacheName) => {
          try {
            await caches.delete(cacheName);
          } catch (error) {
            console.warn(`Cache-Löschung für ${cacheName} fehlgeschlagen:`, error);
          }
        }),
    );
  }
}

export async function register(): Promise<void> {
  if (!('serviceWorker' in navigator)) {
    console.warn('Service Workers are not supported in this browser.');
    return;
  }

  if (!shouldRegisterBrowserServiceWorker()) {
    console.info('Service Worker Registrierung fuer Native-Clients uebersprungen.');
    return;
  }

  if (import.meta.env.DEV && !DEV_SERVICE_WORKER_ENABLED) {
    await cleanupDevelopmentRegistrations();
    console.info('🧪 Entwicklungsmodus erkannt - Service Worker werden deaktiviert, um Vite-HMR sauber zu halten.');
    return;
  }

  try {
    const existingRegistration = await navigator.serviceWorker.getRegistration('/');
    if (existingRegistration?.active?.scriptURL.endsWith('/service-worker.js')) {
      await existingRegistration.update();
      console.log('Service Worker bereits registriert, Update-Pruefung ausgefuehrt:', existingRegistration.scope);
      return;
    }

    if (existingRegistration) {
      await existingRegistration.unregister();
    }

    const registration: ServiceWorkerRegistration = await navigator.serviceWorker.register('/service-worker.js', {
      scope: '/',
      updateViaCache: 'none',
    });
    console.log('Service Worker registered with scope:', registration.scope);

    const existingSub: PushSubscription | null = await registration.pushManager.getSubscription();
    if (existingSub) {
      console.log('Existing subscription:', existingSub);
    } else {
      console.log('No existing subscription.');
    }

    navigator.serviceWorker.addEventListener('message', (event: MessageEvent) => {
      console.log('Message from service worker:', event.data);
    });
  } catch (error) {
    console.error('Service Worker registration failed:', error);
  }
}
